import { BrowserWebSocketClientAdapter } from "@automerge/automerge-repo-network-websocket";
// @ts-expect-error (the slim d.ts omits the wasm-bindgen init default export)
import initSubductionWasm from "@automerge/automerge-subduction/slim";
import subductionWasmUrl from "@automerge/automerge-subduction/wasm?url";
import automergeWasmUrl from "@automerge/automerge/automerge.wasm?url";
import { initializeWasm } from "@automerge/automerge/slim";
import { Repo } from "@automerge/react";
import { SocketResumeMonitor } from "den/client/socket-resume";

import { isEmbedSurface, openStore } from "../local-store";

// Start the wasm fetch + streaming compile at module eval. The client graph is
// all `/slim` (vite.config.ts), so nothing top-level-awaits this — it overlaps
// React mount and route-chunk loading. `?url` keeps the blob SW-precached.
// Exported for secondary repos (the per-chat doc-transport repo) so they wait
// on the SAME init instead of racing a second one.
// Both WASMs: a Repo on the subduction line constructs its Subduction engine
// at construction, so that module must be ready too.
export const wasmReady = Promise.all([
  initializeWasm(automergeWasmUrl),
  initSubductionWasm({ module_or_path: subductionWasmUrl }),
]).then(() => {});

function getSyncUrl(): string {
  const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  // Same-origin in both dev and prod: the Cloudflare worker serves the
  // `/api/sync` WebSocket upgrade in-process (dev via `@cloudflare/vite-plugin`,
  // prod via Workers). No separate Hono server / port to mangle anymore.
  return `${wsProtocol}//${window.location.host}/api/sync`;
}

// Reconnect cadence for a *cleanly* closed socket. The upstream default is 5s,
// but that's a fixed interval with no backoff: when a tenant's HearthDO is
// unhealthy (e.g. resetting under memory pressure), every connected tab retries
// every 5s, and each retry forces a full workspace resync — heavy hydration
// that pushes the DO right back over the edge, denying it room to recover.
// Stretching to 15s cuts that pressure ~3×. It doesn't slow the *common*
// reconnect cases: half-open sockets (sleep/wake, network flap) are handled
// event-driven by SocketResumeMonitor below, which probes and force-closes so a
// fresh connect happens immediately — the periodic retry is only the fallback
// for a hard, sustained failure, which is exactly when gentler is better.
const RECONNECT_RETRY_MS = 15_000;

const wsAdapter = new BrowserWebSocketClientAdapter(
  getSyncUrl(),
  RECONNECT_RETRY_MS,
);

// Half-open sockets (laptop sleep, network flap) don't fire `onclose`, so the
// upstream adapter's reconnect logic never trips. Nudging it on visibility +
// online events catches the common cases without a periodic ping. The
// adapter's `socket` field is real but untyped upstream, hence the cast.
new SocketResumeMonitor(wsAdapter as unknown as { socket?: WebSocket }).start();

// Workspace docs only. A chat doc lives in its own ChatHostDO behind its own
// socket (`useChatDoc`) and its URL is in no workspace doc, so opening a tab
// never pulls the back catalogue of threads.
//
// The embed surface is throwaway, so: no storage, `isEphemeral`. `sharePolicy`
// governs what we offer a peer, not what we adopt from one — an inbound sync
// message for an unknown doc materialises it either way.
//
// Waits on the wasm and the tenant store; paint doesn't — main.tsx renders with
// a null RepoContext and the workspace gates hold consumers back.
export const repoReady: Promise<Repo> = Promise.all([
  wasmReady,
  openStore("tenant"),
]).then(
  ([, storage]) =>
    new Repo(
      isEmbedSurface
        ? {
            sharePolicy: () => Promise.resolve(false),
            isEphemeral: true,
            network: [wsAdapter],
          }
        : { storage, network: [wsAdapter] },
    ),
);
